import { Lock } from "@mui/icons-material";
import { Card, MenuItem, Select, Stack, Typography } from "@mui/material";
import classNames from "classnames";
import React, { type ComponentProps, type Dispatch, type SetStateAction, useEffect, useState } from "react";
import { type MarketDataBrowser, type Input as MarketDataInput } from "~/components/market-data/MarketDataBrowser";
import { CompareFiltersCard } from "~/components/market-data/market-data-filters/CompareFiltersCard";
import { DeletePresetModal } from "~/components/market-data/market-data-filters/DeletePresetsModal";
import { SaveMarketDataFiltersModal } from "~/components/market-data/market-data-filters/SaveMarketDataFiltersModal";
import { JobSelector } from "~/components/ui/JobSelector";
import { LegacyCompanyFilters } from "~/components/ui/LegacyCompanyFilters";
import { LevelSelector } from "~/components/ui/LevelSelector";
import { LocationSelector } from "~/components/ui/LocationSelector";
import { Button } from "~/components/ui/core/Button";
import { Icon } from "~/components/ui/core/Icon";
import { Ping } from "~/components/ui/core/Ping";
import { BenchmarkLevelSelector } from "~/components/ui/level/BenchmarkLevelSelector";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { useSession } from "~/hooks/useSession";
import { useI18n } from "~/lib/i18n/useI18n";
import { getId } from "~/lib/utils";
import { useFetchFiltersPresetsQuery } from "~/pages/api/filters-preset/fetch-filters-presets";
import { type ConvertFiltersPresetsToInputResult } from "~/services/filters-preset/convertFiltersPresetsToInput";
import { type FetchFiltersPresetsResult } from "~/services/filters-preset/fetchFiltersPresets";

type Props = {
  company: ComponentProps<typeof MarketDataBrowser>["company"];
  input: MarketDataInput;
  useTestData: boolean;
  onChange: (input: Partial<MarketDataInput>) => void;
  readOnly: boolean;
  onReadOnlyInstructionsClick: () => void;
  isCollapsed: boolean;
  setIsCollapsed: Dispatch<SetStateAction<boolean>>;
  filtersPresets: ConvertFiltersPresetsToInputResult;
};

export const MarketDataFilters: React.FC<Props> = ({
  company,
  input,
  useTestData,
  readOnly,
  onChange,
  onReadOnlyInstructionsClick,
  isCollapsed,
  setIsCollapsed,
  filtersPresets: initialFiltersPresets,
}) => {
  const { t } = useI18n();
  const { user, updateFlag } = useSession();
  const [showModal, setShowModal] = useState(false);
  const [editPresetsModalOpen, setEditPresetsModalOpen] = useState(false);
  const { CAN_ACCESS_LEVEL_FRAMEWORKS } = useFeatureFlags();

  const canAccessBenchmarkLevels = CAN_ACCESS_LEVEL_FRAMEWORKS || input.useBenchmarkLevelsInShare;
  const { data: filtersPresets } = useFetchFiltersPresetsQuery({
    options: {
      initialData: initialFiltersPresets,
    },
  });

  const [selectedFilterPreset, setSelectedFilterPreset] = useState<FetchFiltersPresetsResult[number] | null>(
    filtersPresets.find((preset) => preset.id === input.presetId) ?? null
  );

  const hasSelectedOneJob = input.market.jobIds.length;

  useEffect(() => {
    if (user && isCollapsed !== user?.flags.collapseMarketDataFilters) {
      void updateFlag({ collapseMarketDataFilters: isCollapsed });
    }
  }, [isCollapsed]);

  useEffect(() => {
    setSelectedFilterPreset(filtersPresets.find((preset) => preset.id === input.presetId) ?? null);
  }, [input]);

  return (
    <div className="mt-2 w-full md:absolute md:w-auto">
      <Button
        variant="contained"
        color="white"
        className={classNames({
          "group min-w-0 origin-top-left duration-150 md:-ml-2 md:h-[72px] md:w-10 md:!rounded-md md:!rounded-l-none":
            true,
          "!absolute -translate-x-full opacity-0": !isCollapsed,
          "translate-x-0 opacity-100": isCollapsed,
        })}
        onClick={() => {
          setIsCollapsed(false);
        }}
      >
        <div className="md:absolute md:left-2.5 md:top-14 md:origin-top-left md:-rotate-90">
          {t("components.market-data-filters.filters")}
        </div>
      </Button>

      <Stack
        className={classNames({
          "w-full rounded-bl-none rounded-tl-none pb-0 transition duration-150 md:w-64 md:shrink-0": true,
          "translate-x-0 opacity-100": !isCollapsed,
          "absolute -translate-x-full opacity-0": isCollapsed,
        })}
        spacing={4}
      >
        <Stack>
          <Card component={Stack} variant="outlined" spacing={2}>
            {readOnly && (
              <div className="-mx-5 !-mt-4 !mb-2 flex items-center justify-between rounded-t border-l-4 border-r-4 border-secondary-600 bg-gradient-to-br from-secondary-400 to-secondary-500 px-1 py-2">
                <Stack direction="row" alignItems="center" className="w-full">
                  <Lock className="mr-1" fontSize="small" />

                  <Typography variant="subtitle2">{t("components.market-data-filters.read-only-mode")}</Typography>

                  <div
                    className="ml-auto mr-1 text-gray-700 transition hover:scale-110 hover:text-gray-900 focus:outline-none"
                    onClick={onReadOnlyInstructionsClick}
                  >
                    <Icon name="information" size="md" />
                  </div>
                </Stack>
              </div>
            )}

            <div className="!mt-0 -ml-1 flex justify-between pb-3 pt-1">
              <Typography textTransform="capitalize" fontWeight="500" color="gray.600">
                {t("components.market-data-filters.filters")}
              </Typography>
              <a
                className="ml-4 cursor-pointer text-xs text-gray-500 hover:scale-125 hover:underline"
                onClick={() => {
                  setIsCollapsed(true);
                }}
              >
                <Icon name="chevron-left" />
              </a>
            </div>

            {!canAccessBenchmarkLevels && filtersPresets.length > 0 && (
              <>
                <Typography variant="subtitle2">{t("components.market-data-filters.presets")}</Typography>
                <Select
                  name={t("common.name")}
                  value={selectedFilterPreset?.id ?? "placeholder"}
                  onChange={(event) => {
                    const preset = filtersPresets.find((preset) => preset.id === event.target.value);

                    if (!preset) return;
                    setSelectedFilterPreset(preset);

                    onChange({
                      ...input,
                      levels: preset.input.levels ?? input.levels,
                      mergeAdvancedLevels: preset.input.mergeAdvancedLevels ?? input.mergeAdvancedLevels,
                      percentiles: preset.input.percentiles ?? input.percentiles,
                      presetId: event.target.value as number,
                      market: {
                        ...input.market,
                        ...preset.market,
                      },
                    });
                  }}
                  renderValue={(value) => {
                    return (
                      <Typography className={classNames({ "text-gray-500": value === "placeholder" })}>
                        {value === "placeholder"
                          ? t("pages.market-data.market-data-filters.presets-placeholder")
                          : selectedFilterPreset?.name}
                      </Typography>
                    );
                  }}
                >
                  {filtersPresets.map((preset) => {
                    const itemValue = preset.id;

                    return (
                      <MenuItem value={itemValue} key={itemValue}>
                        {preset.name}
                      </MenuItem>
                    );
                  })}

                  <MenuItem
                    value="edit-presets"
                    onClick={() => {
                      setEditPresetsModalOpen(true);
                    }}
                    className="justify-center text-sm text-secondary-600"
                  >
                    {t("components.market-data-filters.edit-presets")}
                  </MenuItem>
                </Select>
                <DeletePresetModal
                  isOpen={editPresetsModalOpen}
                  onClose={() => {
                    setEditPresetsModalOpen(false);
                  }}
                />
              </>
            )}

            <Stack spacing={3}>
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2">
                  {!hasSelectedOneJob && <Ping color="secondary" />}

                  <Typography variant="subtitle2">
                    {!hasSelectedOneJob
                      ? t("components.market-data-filters.select-at-least-one-job")
                      : t("components.market-data-filters.job")}
                  </Typography>
                </div>

                <div className="flex flex-col space-y-2">
                  <JobSelector
                    className="w-full"
                    enableCustomJobs
                    multiple
                    disabled={readOnly}
                    jobIds={input.market.jobIds}
                    onChange={(jobIds) => {
                      onChange({ market: { ...input.market, jobIds } });
                    }}
                  />
                </div>
              </div>

              <div className="flex flex-col space-y-2">
                <Typography variant="subtitle2">{t("components.market-data-filters.level")}</Typography>

                {!canAccessBenchmarkLevels ? (
                  <LevelSelector
                    disabled={readOnly}
                    includeMetaLevels
                    multiple
                    noFilter={useTestData}
                    value={input.levels}
                    onChange={(event) => {
                      onChange({ levels: event.target.value });
                    }}
                    useAdvancedLevels={user?.company.useAdvancedLevels ?? true}
                  />
                ) : (
                  <BenchmarkLevelSelector
                    multiple
                    disabled={readOnly}
                    useBenchmarkLevelsInShare={input.useBenchmarkLevelsInShare}
                    value={input.benchmarkLevelIds}
                    onChange={(event) => {
                      onChange({ benchmarkLevelIds: event.target.value.map(getId) });
                    }}
                  />
                )}
              </div>

              <div className="flex flex-col space-y-2">
                <Typography variant="subtitle2">{t("components.market-data-filters.location")}</Typography>

                <LocationSelector
                  multiple
                  dense={false}
                  disabled={readOnly}
                  value={input.market.locations}
                  onChange={(event) => {
                    onChange({
                      market: {
                        ...input.market,
                        locations: event.target.value,
                      },
                    });
                  }}
                />
              </div>

              <Stack spacing={3}>
                <Typography textTransform="capitalize" fontWeight="500" color="gray.600">
                  {t("pages.market-data.market-data-filters.market-filters")}
                </Typography>
                <LegacyCompanyFilters
                  company={company}
                  disabled={readOnly}
                  input={input.market}
                  onChange={(value) => {
                    onChange({ market: { ...input.market, ...value } });
                  }}
                />
              </Stack>
            </Stack>
          </Card>
          <Button
            variant="contained"
            color="white"
            className=" -translate-y-1 !border-t-0 md:ml-32 md:w-32 md:!rounded-t-none md:border-t-0"
            sx={{
              "::before": {
                content: '""',
                position: "absolute",
                top: "-2px",
                left: "-5px",
                height: "5px",
                width: "10px",
                backgroundColor: "#fff",
              },
            }}
            onClick={() => {
              setShowModal(true);
            }}
          >
            {t("components.market-data-filters.save-your-filters")}
          </Button>

          {showModal && (
            <SaveMarketDataFiltersModal isOpen={showModal} onClose={() => setShowModal(false)} input={input} />
          )}
        </Stack>

        <CompareFiltersCard company={company} input={input} readOnly={readOnly} onChange={onChange} />
      </Stack>
    </div>
  );
};
